import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ExternalLink, Radio } from 'lucide-react';
import {
  listLscLiveDashboard,
  mapLiveDashboardRowToLeagueGame,
  resolveLiveStreamKey,
} from '../../api';
import type { LscArchiveItem, LscLiveLeagueGame } from '../../types';
import { lscPath } from '../../routing';
import { displaySeasonLabel } from '../../seasonDisplay';
import { useLeague } from '../../hooks/useLeague';
import CfbMatchupChips from '../CfbMatchupChips';
import LiveGameEmbedPanel, {
  formatLiveGameBroadcasterLabel,
  formatLiveGameMatchupLabel,
} from '../LiveGameEmbedPanel';
import {
  getLiveGameStatusBadge,
  isQualifiedLikelyLiveGame,
  isSafeTwitchWatchUrl,
  isVerifiedLiveGame,
  liveStreamKeyTestId,
} from '../LiveLeagueGamesCard';
import {
  getLiveHeroExternalCtaLabel,
  getLiveHeroSectionHeading,
  getLiveHeroSectionSubtitle,
  getMediaHomeLiveGames,
  isLikelyOnlyFeaturedGame,
  partitionMediaHomeLiveGames,
} from './leagueHomeUtils';
import ArchiveReplayCard from './ArchiveReplayCard';
import { useLeagueHomeLivePolling } from '../../hooks/useLeagueHomeLivePolling';

export type LeagueHomeHeroViewProps = {
  leagueId: string;
  liveGames: LscLiveLeagueGame[];
  featuredGame: LscLiveLeagueGame | null;
  secondaryGames: LscLiveLeagueGame[];
  fallbackArchive: LscArchiveItem | null;
  suppressArchiveFallback?: boolean;
  loading: boolean;
  error: string | null;
  selectedLiveStreamKey: string | null;
  onSelectGame: (liveStreamKey: string) => void;
  onRefresh: () => void;
};

function LiveHeroMeta({
  game,
  isLikelyOnlyFeatured = false,
}: {
  game: LscLiveLeagueGame;
  isLikelyOnlyFeatured?: boolean;
}) {
  const badge = getLiveGameStatusBadge(game);
  const { league } = useLeague();
  const stageParts = [
    game.stage_label?.trim(),
    displaySeasonLabel(game.season_label),
  ].filter(Boolean);
  const showLikelyCopy = isLikelyOnlyFeatured || isQualifiedLikelyLiveGame(game);
  const showVerifiedCopy = isVerifiedLiveGame(game);
  const awayLabel = game.away_team?.trim() || 'Away';
  const homeLabel = game.home_team?.trim() || 'Home';
  const showNeutralMatchup =
    game.matchup_display_mode === 'neutral' || game.matchup_label_suppressed;

  return (
    <div className="space-y-2" data-testid="league-home-hero-meta">
      <div className="flex flex-wrap items-center gap-2">
        <Radio
          className={`h-4 w-4 ${showLikelyCopy && !showVerifiedCopy ? 'text-sky-400' : 'text-red-400'}`}
          aria-hidden
        />
        <span
          className={`text-sm font-medium ${
            showLikelyCopy && !showVerifiedCopy ? 'text-sky-200' : 'text-red-200'
          }`}
          data-testid="league-home-hero-headline"
        >
          {showLikelyCopy && !showVerifiedCopy ? 'Likely live' : 'Live now'}
        </span>
        {badge ? (
          <span className={badge.className} data-testid={badge.testId}>
            {badge.label}
          </span>
        ) : null}
      </div>
      {showLikelyCopy && !showVerifiedCopy ? (
        <p className="text-xs text-secondary-400" data-testid="league-home-hero-likely-disclaimer">
          Verification in progress — stream not fully confirmed yet.
        </p>
      ) : null}
      <h2 className="text-2xl font-bold text-white">
        {showNeutralMatchup ? (
          'Live verification in progress'
        ) : league?.game_key === 'cfb' ? (
          <CfbMatchupChips
            awayLabel={awayLabel}
            homeLabel={homeLabel}
            separator="vs"
            size="md"
            showLabels
          />
        ) : (
          formatLiveGameMatchupLabel(game)
        )}
      </h2>
      <p className="text-sm text-secondary-300">
        {formatLiveGameBroadcasterLabel(game)}
        {stageParts.length > 0 ? ` · ${stageParts.join(' · ')}` : ''}
      </p>
    </div>
  );
}

function SecondaryLiveCard({
  game,
  isSelected,
  onSelect,
}: {
  game: LscLiveLeagueGame;
  isSelected: boolean;
  onSelect: (key: string) => void;
}) {
  const streamKey = resolveLiveStreamKey(game);
  const badge = getLiveGameStatusBadge(game);
  const { league } = useLeague();
  const awayLabel = game.away_team?.trim() || 'Away';
  const homeLabel = game.home_team?.trim() || 'Home';
  const showNeutralMatchup =
    game.matchup_display_mode === 'neutral' || game.matchup_label_suppressed;

  return (
    <button
      type="button"
      onClick={() => onSelect(streamKey)}
      className={`shrink-0 w-56 rounded-lg border px-3 py-3 text-left transition-colors ${
        isSelected
          ? 'border-primary-500 bg-primary-500/10'
          : 'border-secondary-700 bg-secondary-800/40 hover:border-secondary-600'
      }`}
      data-testid={`league-home-live-secondary-${liveStreamKeyTestId(streamKey)}`}
      aria-pressed={isSelected}
    >
      <div className="flex flex-wrap items-center gap-2 mb-1">
        {badge ? (
          <span className={badge.className} data-testid={badge.testId}>
            {badge.label}
          </span>
        ) : null}
      </div>
      <div className="text-sm font-medium text-white">
        {showNeutralMatchup ? (
          'Live verification in progress'
        ) : league?.game_key === 'cfb' ? (
          <CfbMatchupChips awayLabel={awayLabel} homeLabel={homeLabel} separator="vs" size="sm" />
        ) : (
          formatLiveGameMatchupLabel(game)
        )}
      </div>
      <p className="text-xs text-secondary-400 mt-1">{formatLiveGameBroadcasterLabel(game)}</p>
    </button>
  );
}

export function LeagueHomeHeroView({
  leagueId,
  liveGames,
  featuredGame,
  secondaryGames,
  fallbackArchive,
  suppressArchiveFallback = false,
  loading,
  error,
  selectedLiveStreamKey,
  onSelectGame,
  onRefresh,
}: LeagueHomeHeroViewProps) {
  const selectedGame =
    liveGames.find((game) => resolveLiveStreamKey(game) === selectedLiveStreamKey) ??
    featuredGame;
  const ctaGame = selectedGame ?? featuredGame;
  const sectionHeading =
    suppressArchiveFallback && !featuredGame
      ? 'Likely Live'
      : getLiveHeroSectionHeading(liveGames, featuredGame);
  const sectionSubtitle =
    suppressArchiveFallback && !featuredGame
      ? 'Verification in progress — a possible live stream was detected.'
      : getLiveHeroSectionSubtitle(liveGames, featuredGame);

  return (
    <section
      className="card space-y-4"
      data-testid="league-home-hero"
      aria-live="polite"
      aria-busy={loading}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2
            className="text-lg font-semibold text-white"
            data-testid="league-home-hero-section-title"
          >
            {sectionHeading}
          </h2>
          <p className="text-sm text-secondary-400 mt-1" data-testid="league-home-hero-section-subtitle">
            {sectionSubtitle}
          </p>
        </div>
        <button type="button" className="btn-secondary text-sm" onClick={onRefresh}>
          Refresh
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-secondary-400" data-testid="league-home-hero-loading">
          Checking for live league games...
        </p>
      ) : error ? (
        <div className="space-y-2" data-testid="league-home-hero-error">
          <p className="text-sm text-red-300">{error}</p>
          <button type="button" className="btn-secondary text-sm" onClick={onRefresh}>
            Try again
          </button>
        </div>
      ) : featuredGame ? (
        <div className="space-y-4" data-testid="league-home-hero-live">
          <LiveHeroMeta
            game={featuredGame}
            isLikelyOnlyFeatured={isLikelyOnlyFeaturedGame(featuredGame)}
          />
          {selectedGame ? <LiveGameEmbedPanel game={selectedGame} /> : null}
          {secondaryGames.filter(isVerifiedLiveGame).length > 0 ? (
            <div className="space-y-2" data-testid="league-home-hero-multi">
              <p className="text-xs text-secondary-500">More live league games</p>
              <div className="flex gap-3 overflow-x-auto pb-1">
                {[featuredGame, ...secondaryGames.filter(isVerifiedLiveGame)]
                  .filter(
                    (game, index, arr) =>
                      arr.findIndex((g) => resolveLiveStreamKey(g) === resolveLiveStreamKey(game)) ===
                      index
                  )
                  .map((game) => (
                    <SecondaryLiveCard
                      key={resolveLiveStreamKey(game)}
                      game={game}
                      isSelected={resolveLiveStreamKey(game) === selectedLiveStreamKey}
                      onSelect={onSelectGame}
                    />
                  ))}
              </div>
            </div>
          ) : null}
          {secondaryGames.filter(isQualifiedLikelyLiveGame).length > 0 ? (
            <div className="space-y-2" data-testid="league-home-hero-likely-section">
              <p className="text-xs text-secondary-500">Likely live — verification in progress</p>
              <div className="flex gap-3 overflow-x-auto pb-1">
                {secondaryGames
                  .filter(isQualifiedLikelyLiveGame)
                  .filter(
                    (game, index, arr) =>
                      arr.findIndex((g) => resolveLiveStreamKey(g) === resolveLiveStreamKey(game)) ===
                      index
                  )
                  .map((game) => (
                    <SecondaryLiveCard
                      key={resolveLiveStreamKey(game)}
                      game={game}
                      isSelected={resolveLiveStreamKey(game) === selectedLiveStreamKey}
                      onSelect={onSelectGame}
                    />
                  ))}
              </div>
            </div>
          ) : isLikelyOnlyFeaturedGame(featuredGame) && secondaryGames.length > 0 ? (
            <div className="space-y-2" data-testid="league-home-hero-likely-section">
              <p className="text-xs text-secondary-500">More likely live games</p>
              <div className="flex gap-3 overflow-x-auto pb-1">
                {secondaryGames.map((game) => (
                  <SecondaryLiveCard
                    key={resolveLiveStreamKey(game)}
                    game={game}
                    isSelected={resolveLiveStreamKey(game) === selectedLiveStreamKey}
                    onSelect={onSelectGame}
                  />
                ))}
              </div>
            </div>
          ) : null}
          {ctaGame && isSafeTwitchWatchUrl(ctaGame.watch_url) ? (
            <a
              href={ctaGame.watch_url!}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-sm text-primary-300 hover:text-primary-200"
              data-testid="league-home-watch-live-external"
            >
              <ExternalLink className="h-4 w-4" aria-hidden />
              {getLiveHeroExternalCtaLabel(ctaGame)}
            </a>
          ) : null}
        </div>
      ) : suppressArchiveFallback ? (
        <div className="space-y-3" data-testid="league-home-hero-verification-pending">
          <p className="text-sm text-secondary-300">A league game stream is live.</p>
          <h2 className="text-xl font-semibold text-white">Live verification in progress</h2>
          <p className="text-sm text-secondary-500">
            Matchup details will appear here once the live game is verified for League Home.
          </p>
        </div>
      ) : fallbackArchive ? (
        <div className="space-y-4" data-testid="league-home-hero-fallback">
          <div className="space-y-2">
            <p className="text-sm text-secondary-300">No league games are live right now.</p>
            <h2 className="text-xl font-semibold text-white">Catch the latest replay</h2>
          </div>
          <ArchiveReplayCard leagueId={leagueId} archive={fallbackArchive} />
        </div>
      ) : (
        <div className="space-y-3" data-testid="league-home-hero-empty">
          <p className="text-sm text-secondary-300">No live games yet.</p>
          <p className="text-sm text-secondary-500">
            When a qualified league stream goes live, it will appear here.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to={lscPath(`/leagues/${leagueId}/matches`)} className="btn-secondary text-sm">
              See full schedule
            </Link>
            <Link to={lscPath(`/leagues/${leagueId}/archive`)} className="btn-secondary text-sm">
              View archive
            </Link>
          </div>
        </div>
      )}
    </section>
  );
}

export type LeagueHomeHeroProps = {
  leagueId: string;
  fallbackArchive: LscArchiveItem | null;
};

const LeagueHomeHero: React.FC<LeagueHomeHeroProps> = ({ leagueId, fallbackArchive }) => {
  const [liveGames, setLiveGames] = useState<LscLiveLeagueGame[]>([]);
  const [suppressArchiveFallback, setSuppressArchiveFallback] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLiveStreamKey, setSelectedLiveStreamKey] = useState<string | null>(null);

  const loadLive = useCallback(async () => {
    setError(null);
    try {
      const dashboard = await listLscLiveDashboard(leagueId);
      const memberVisibleRows = (dashboard.member_visible_rows ?? []).map(
        mapLiveDashboardRowToLeagueGame
      );
      setLiveGames(getMediaHomeLiveGames(memberVisibleRows));
      setSuppressArchiveFallback(Boolean(dashboard.suppress_archive_fallback));
    } catch {
      setError('Could not load live league games.');
      setSuppressArchiveFallback(false);
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    void loadLive();
  }, [loadLive]);

  const hasLiveActivity = liveGames.length > 0;
  useLeagueHomeLivePolling(() => loadLive(), hasLiveActivity);

  const livePartition = useMemo(() => partitionMediaHomeLiveGames(liveGames), [liveGames]);
  const featuredGame = livePartition.featuredGame;
  const secondaryGames = livePartition.secondaryGames;
  const allLiveGames = livePartition.allLiveGames;

  useEffect(() => {
    if (allLiveGames.length === 0) {
      setSelectedLiveStreamKey(null);
      return;
    }
    setSelectedLiveStreamKey((current) => {
      const keys = allLiveGames.map((game) => resolveLiveStreamKey(game));
      if (current && keys.includes(current)) return current;
      return keys[0] ?? null;
    });
  }, [allLiveGames]);

  const viewProps = useMemo(
    () => ({
      leagueId,
      liveGames: allLiveGames,
      featuredGame,
      secondaryGames,
      fallbackArchive,
      suppressArchiveFallback,
      loading,
      error,
      selectedLiveStreamKey,
      onSelectGame: setSelectedLiveStreamKey,
      onRefresh: () => {
        void loadLive();
      },
    }),
    [
      leagueId,
      allLiveGames,
      featuredGame,
      secondaryGames,
      fallbackArchive,
      suppressArchiveFallback,
      loading,
      error,
      selectedLiveStreamKey,
      loadLive,
    ]
  );

  return <LeagueHomeHeroView {...viewProps} />;
};

export default LeagueHomeHero;
